const _clamp = (value: number, min: number, max: number) => Math.max(Math.min(value, max), min);

const DEFAULT_FILE_SIZE = 1024 * 1024 * 50; /* 50MiB */

const _responseWithProgressCallbacks = async (
    response: Response & { body: ReadableStream<Uint8Array> },
    callback?: (progress: number) => void,
): Promise<Response> => {
    const contentLength = parseInt(
        response.headers.get('Content-Length') ?? `${DEFAULT_FILE_SIZE}`,
    );

    const stream = new ReadableStream({
        start: async controller => {
            const reader = response.body.getReader();
            let readerIndex = 0;
            let lastProgress = 0;

            while (readerIndex < contentLength) {
                const { done, value } = await reader.read().catch(error => {
                    controller.error(error);
                    return { done: true, value: null };
                });

                readerIndex += value?.length ?? 0;

                if (done) {
                    readerIndex = contentLength;
                }

                const newProgress = _clamp(readerIndex / contentLength, 0, 1);
                const progressDelta = newProgress - lastProgress;

                /* Message 1% delta min */
                if (callback && progressDelta > 0.1) {
                    callback(newProgress);
                    lastProgress = newProgress;
                }

                controller.enqueue(value);
            }

            controller.close();
        },
    });

    return new Response(stream, response);
};

const fetchWithProgress = async (
    request: Request,
    timeout?: number,
    callback?: (progress: number) => void,
): Promise<Response> => {
    const controller = new AbortController();

    const timeoutId = setTimeout(() => controller.abort(), timeout ?? 300 * 1000);

    /* https://github.com/GoogleChrome/workbox/issues/1796 */
    const response = await fetch(
        request,
        request.mode === 'navigate' ? undefined : { signal: controller.signal },
    );

    const hasProgressSupport = response.body !== null;
    const isNotCorsRequest =
        request.mode !==
        'cors'; /* Cannot set `Response.type` in `new Response` hence CORS is not supported */

    const responseReturn =
        hasProgressSupport && !isNotCorsRequest
            ? _responseWithProgressCallbacks(
                  response as Response & { body: ReadableStream<Uint8Array> },
                  callback,
              )
            : response;

    clearTimeout(timeoutId);

    return responseReturn;
};

const module = {
    fetchWithProgress,
};

export default module;
